import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, Clock, CreditCard } from 'lucide-react';
import { checkSubscriptionStatus, getHospitalSubscription, getDaysRemaining } from '@/lib/subscription';
import { getCurrentHospitalId } from '@/lib/hospitalContext';
import { useAuthStore } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

export function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const [isChecking, setIsChecking] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [missingHospitalLink, setMissingHospitalLink] = useState(false);
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        setMissingHospitalLink(true);
        setIsActive(false);
        return;
      }

      setMissingHospitalLink(false);
      const active = await checkSubscriptionStatus(hospitalId);
      setIsActive(active);

      if (!active) {
        const subscription = await getHospitalSubscription();
        setSubscriptionDetails(subscription);

        if (subscription) {
          const remaining = getDaysRemaining(subscription);
          setDaysRemaining(remaining.daysRemaining);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setIsChecking(false);
    }
  };

  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking subscription...</p>
        </div>
      </div>
    );
  }

  if (!isActive) {
    const isExpired = !missingHospitalLink && daysRemaining === 0;
    const isTrial = subscriptionDetails?.subscription_status === 'trial';

    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <div className="flex items-center gap-2">
              {isExpired || missingHospitalLink ? (
                <AlertCircle className="h-8 w-8 text-destructive" />
              ) : (
                <Clock className="h-8 w-8 text-yellow-600" />
              )}
              <CardTitle className="text-2xl">
                {missingHospitalLink
                  ? 'Account Not Linked to Hospital'
                  : isExpired
                  ? 'Subscription Expired'
                  : `Trial Ending Soon`}
              </CardTitle>
            </div>
            <CardDescription>
              {missingHospitalLink
                ? 'Your user account is not linked to your hospital yet. Ask your hospital administrator to fix your account in User Management, then try again.'
                : isExpired
                ? isAdmin
                  ? 'Your hospital subscription has expired. Please renew to continue using the service.'
                  : 'Your hospital subscription has expired. Please contact your hospital administrator to renew the plan.'
                : isTrial
                ? `Your trial expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`
                : `Your subscription expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isExpired && !missingHospitalLink && (
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertTitle>Active Until</AlertTitle>
                <AlertDescription>
                  {subscriptionDetails?.subscription_expires_at
                    ? new Date(subscriptionDetails.subscription_expires_at).toLocaleDateString()
                    : subscriptionDetails?.trial_started_at && subscriptionDetails?.trial_days
                    ? new Date(
                        new Date(subscriptionDetails.trial_started_at).getTime() +
                          subscriptionDetails.trial_days * 24 * 60 * 60 * 1000
                      ).toLocaleDateString()
                    : 'Unknown'}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="flex-1"
              >
                Check Again
              </Button>
              {isAdmin && !missingHospitalLink && (
                <Button
                  onClick={() => navigate('/subscription')}
                  className="flex-1"
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  View Plans
                </Button>
              )}
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="w-full mt-2"
            >
              Return to Dashboard
            </Button>

            {(isExpired || missingHospitalLink) && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Access Restricted</AlertTitle>
                <AlertDescription>
                  {missingHospitalLink
                    ? 'Staff and doctors use the same hospital plan as the admin. Your administrator must assign you to the hospital in Settings → User Management.'
                    : isAdmin
                    ? 'Please renew your subscription to regain access to all features.'
                    : 'All users in your hospital share one subscription. Contact your hospital administrator to renew the plan.'}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}

